import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { SAME_SITE_STRICT } from '@constants/cookies';
import { IS_DEV_NODE_ENV } from '@constants/nodeEnvs';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import { type ThemeColor, useWebviewBridge, useWebviewEvents } from '@contexts/webview-bridge';
import Cookies from 'js-cookie'; // Import js-cookie

const DARK_MODE_COOKIE = 'xceed-dark-mode';
const cookieDomain = IS_DEV_NODE_ENV ? 'localhost' : '.xceed.me';
// Typed with `#${ThemeColor}` so the constants stay in lock-step with the bridge
// union at compile time: any future hex change that drifts from `ThemeColor`
// becomes a tsc error here, not a silent runtime lie.
const LIGHT_THEME_COLOR: `#${ThemeColor}` = '#FFFFFF';
const DARK_THEME_COLOR: `#${ThemeColor}` = '#242426';

// Strip the `#` and uppercase to match the format mobivery expects. The cast
// is provably safe: the input is restricted to `#${ThemeColor}`, so the output
// is always a `ThemeColor` literal.
const toBridgeHex = (color: `#${ThemeColor}`): ThemeColor => color.replace('#', '').toUpperCase() as ThemeColor;

const updateThemeMeta = (isDark: boolean) => {
  document.documentElement.style.colorScheme = isDark ? 'dark' : 'light';
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) {
    meta.content = isDark ? DARK_THEME_COLOR : LIGHT_THEME_COLOR;
  }
};

export const DarkModeContext = createContext({
  isDarkMode: false,
  toggleDarkMode: (_e?: React.MouseEvent | React.KeyboardEvent) => {},
  isActive: false,
});

export const DarkModeProvider = ({ children }: { children: ReactNode }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const { isDarkModeEnabled: isActive } = useContext(FeatureFlagContext);
  const { sendChangeThemeColor } = useWebviewEvents();
  const { isInitialized: isBridgeInitialized } = useWebviewBridge();
  // Tracks whether we've already pushed the resolved initial theme to native.
  // Without it the init effect would re-emit every time `isDarkMode` changes
  // (which happens on toggle), spamming the bridge with duplicates.
  const initialThemeEmittedRef = useRef(false);

  const toggleDarkMode = (e?: React.MouseEvent | React.KeyboardEvent) => {
    const newDarkMode = !isDarkMode;

    const applyDarkMode = () => {
      setIsDarkMode(newDarkMode);

      if (newDarkMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }

      updateThemeMeta(newDarkMode);
      // Fire-and-forget bridge notification so the xceedPro native shell can
      // repaint its system chrome (status bar, nav bar) to match the web
      // background. `.catch` is defensive — toggles are user-driven so the
      // bridge will be initialized in practice, but keeps the shape consistent
      // with the init-effect emission below where the race actually matters.
      sendChangeThemeColor({
        color: toBridgeHex(newDarkMode ? DARK_THEME_COLOR : LIGHT_THEME_COLOR),
      }).catch((): void => undefined);

      Cookies.set(DARK_MODE_COOKIE, newDarkMode.toString(), {
        domain: cookieDomain,
        path: '/',
        sameSite: SAME_SITE_STRICT,
        secure: true,
        expires: 3650,
      });
    };

    const prefersReducedMotion = globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!document.startViewTransition || prefersReducedMotion) {
      applyDarkMode();
      return;
    }

    if (e) {
      let x: number, y: number;

      if ('clientX' in e && e.clientX !== 0) {
        x = e.clientX;
        y = e.clientY;
      } else {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        x = rect.left + rect.width / 2;
        y = rect.top + rect.height / 2;
      }

      document.documentElement.style.setProperty('--vt-x', `${x}px`);
      document.documentElement.style.setProperty('--vt-y', `${y}px`);
    }

    document.startViewTransition(applyDarkMode);
  };

  const determineInitialDarkMode = () => {
    const prefersDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const darkModeCookie = Cookies.get(DARK_MODE_COOKIE); // Retrieve value from cookies

    if (isActive) {
      if (darkModeCookie === undefined) {
        return prefersDarkMode;
      } else {
        return darkModeCookie === 'true';
      }
    } else {
      return false;
    }
  };

  const initialDarkMode = determineInitialDarkMode();

  // Sync the resolved initial theme (cookie / system preference) into React
  // state and the DOM. Runs on mount and whenever the resolution changes.
  useEffect(() => {
    if (initialDarkMode !== isDarkMode) {
      setIsDarkMode(initialDarkMode);
      if (initialDarkMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      updateThemeMeta(initialDarkMode);
    }
  }, [initialDarkMode, isDarkMode]);

  // Emit the resolved initial theme to native exactly once, the first time
  // the bridge is ready. WebviewBridgeProvider's own init effect runs AFTER
  // DarkModeProvider's effects on first paint (children-first), so the initial
  // render sees `isBridgeInitialized === false`; once the bridge flips to ready,
  // this effect re-runs and emits. The ref guards against re-emit if
  // `initialDarkMode` is ever recomputed (e.g. feature-flag flip mid-session).
  // Toggles emit independently inside `applyDarkMode` above.
  useEffect(() => {
    if (!isBridgeInitialized || initialThemeEmittedRef.current) return;
    initialThemeEmittedRef.current = true;
    sendChangeThemeColor({
      color: toBridgeHex(initialDarkMode ? DARK_THEME_COLOR : LIGHT_THEME_COLOR),
    }).catch((): void => undefined);
  }, [isBridgeInitialized, initialDarkMode, sendChangeThemeColor]);

  return (
    <DarkModeContext.Provider value={{ isDarkMode, toggleDarkMode, isActive }}>{children}</DarkModeContext.Provider>
  );
};
